import { useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import { useCallback, useMemo } from "react";
import { useFirebaseAuth } from "@/hooks/useFirebaseAuth";
import { i18n } from "@/i18n";
import { authedFetch } from "@/lib/api";
import { extractErrorData } from "@/lib/api-error";
import { eden } from "@/lib/eden";
import { edenData } from "@/lib/eden-data";
import { useNotifyApiError } from "@/lib/notify-api-error";
import { queryKeys } from "@/lib/query-keys";
import { getApiError } from "@/types/api";
import type { Channel } from "@/types/channel";

export type ToggleResult =
  | { ok: true }
  | { ok: false; reason: "token-invalid"; platform: string; channelId: string }
  | { ok: false; reason: "transient" }
  | { ok: false; reason: "other" };

async function callAuthUrl(endpoint: string): Promise<string> {
  const res = await authedFetch(`${import.meta.env.VITE_API_BASE_URL}${endpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error("Failed to get auth URL");
  const body = await res.json();
  return body.data.authUrl;
}

export function useChannels() {
  const { isAuthenticated } = useFirebaseAuth();
  const { message } = App.useApp();
  const notify = useNotifyApiError();
  const queryClient = useQueryClient();
  const query = useQuery<Channel[]>({
    queryKey: queryKeys.channels.all,
    queryFn: async () => {
      const data = edenData(await eden.v1.channels.get());
      return Array.isArray(data) ? data : [];
    },
    enabled: isAuthenticated,
  });
  const channels = query.data ?? [];

  const refreshChannels = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.channels.all });
  }, [queryClient]);

  const connectLine = useCallback(
    async (values: { name: string; channelAccessToken: string; channelSecret: string }) => {
      edenData(await eden.v1.line.connect.post(values));
      message.success(i18n.t("page.lineConnected", { ns: "channels" }));
      void refreshChannels();
    },
    [message, refreshChannels],
  );

  const connectFacebook = useCallback(async () => {
    return callAuthUrl("/v1/facebook/auth-url");
  }, []);

  const connectInstagram = useCallback(async () => {
    return callAuthUrl("/v1/instagram/auth-url");
  }, []);

  const connectTikTok = useCallback(async () => {
    const data = edenData(await eden.v1.tiktok.connect.post({}));
    return data.authUrl;
  }, []);

  const editChannel = useCallback(
    async (channelId: string, values: { name: string }) => {
      edenData(await eden.v1.channels({ id: channelId }).patch(values));
      message.success(i18n.t("page.nameUpdated", { ns: "channels" }));
      void refreshChannels();
    },
    [message, refreshChannels],
  );

  const toggleActive = useCallback(
    async (channelId: string): Promise<ToggleResult> => {
      const channel = channels.find((c) => c.id === channelId);
      if (!channel) return { ok: false, reason: "other" };
      const nextActive = !channel.active;

      // Optimistic flip
      queryClient.setQueryData<Channel[]>(queryKeys.channels.all, (current = []) =>
        current.map((item) => (item.id === channelId ? { ...item, active: nextActive } : item)),
      );

      try {
        edenData(await eden.v1.channels({ id: channelId }).patch({ active: nextActive }));
        message.success(i18n.t(nextActive ? "page.activated" : "page.deactivated", { ns: "channels" }));
        void refreshChannels();
        return { ok: true };
      } catch (err) {
        // Rollback optimistic flip
        queryClient.setQueryData<Channel[]>(queryKeys.channels.all, (current = []) =>
          current.map((item) => (item.id === channelId ? { ...item, active: channel.active } : item)),
        );

        const code = getApiError(err)?.code;
        if (code === "TOKEN_INVALID") {
          const data = extractErrorData(err);
          const platform = typeof data?.platform === "string" ? data.platform : channel.platform;
          const cid = typeof data?.channelId === "string" ? data.channelId : channelId;
          // Refresh so the row picks up the new tokenInvalidAt badge state.
          void refreshChannels();
          return {
            ok: false,
            reason: "token-invalid",
            platform,
            channelId: cid,
          };
        }
        if (code === "TOKEN_CHECK_FAILED") {
          notify(err, {
            fallbackMessage: i18n.t("page.statusCheckFailed", { ns: "channels" }),
          });
          return { ok: false, reason: "transient" };
        }
        notify(err, { fallbackMessage: i18n.t("page.toggleFailed", { ns: "channels" }) });
        return { ok: false, reason: "other" };
      }
    },
    [channels, message, notify, queryClient, refreshChannels],
  );

  const lineChannels = useMemo(() => channels.filter((c) => c.platform === "line"), [channels]);

  const facebookChannels = useMemo(() => channels.filter((c) => c.platform === "facebook"), [channels]);

  const instagramChannels = useMemo(() => channels.filter((c) => c.platform === "instagram"), [channels]);

  const tiktokChannels = useMemo(() => channels.filter((c) => c.platform === "tiktok"), [channels]);

  return {
    channels,
    lineChannels,
    facebookChannels,
    instagramChannels,
    tiktokChannels,
    loading: isAuthenticated && query.isPending,
    error: query.isError ? i18n.t("page.loadFailed", { ns: "channels" }) : null,
    connectLine,
    connectFacebook,
    connectInstagram,
    connectTikTok,
    editChannel,
    toggleActive,
  };
}
